import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES, ONBOARDING_STEPS, INDUSTRIES, COMPANY_SIZES, TARGET_ROLES, TONES_OF_VOICE, VERTICAL_NICHES } from '@traction/shared';
import type { OnboardingStep } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';
import { updateProfile, updateOnboardingStep } from '@traction/api-client';
import { useToast } from '@/components/Toast';
import { Check } from 'lucide-react';

const STEP_LABELS: Record<OnboardingStep, string> = {
  account: 'Account',
  founder_profile: 'Your Profile',
  company_profile: 'Company',
  icp_definition: 'Ideal Customer',
  outreach_preferences: 'Outreach',
  completed: 'Done',
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(
    profile?.onboarding_step || 'account'
  );
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    linkedin_url: profile?.linkedin_url || '',
    company_name: profile?.company_name || '',
    company_industry: profile?.company_industry || [],
    company_stage: profile?.company_stage || '',
    company_website: profile?.company_website || '',
    target_industries: profile?.target_industries || [],
    target_company_sizes: profile?.target_company_sizes || [],
    target_roles: profile?.target_roles || [],
    target_funding_stages: profile?.target_funding_stages || [],
    vertical_niche: profile?.vertical_niche || '',
    tone_of_voice: profile?.tone_of_voice || 'friendly',
    email_signature: profile?.email_signature || '',
  });

  async function handleNext() {
    if (!user) return;
    setLoading(true);

    try {
      const stepIndex = ONBOARDING_STEPS.indexOf(currentStep);
      const nextStep = ONBOARDING_STEPS[stepIndex + 1] as OnboardingStep;

      await updateProfile(user.uid, formData);
      await updateOnboardingStep(user.uid, nextStep);
      await refreshProfile();

      if (nextStep === 'completed') {
        navigate(ROUTES.DASHBOARD);
      } else {
        setCurrentStep(nextStep);
      }
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Could not save your progress', 'Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  function updateField<K extends keyof typeof formData>(key: K, value: typeof formData[K]) {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }

  function toggleArrayField(key: 'company_industry' | 'target_industries' | 'target_company_sizes' | 'target_roles' | 'target_funding_stages', value: string) {
    setFormData((prev) => {
      const current = prev[key] as string[];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [key]: updated };
    });
  }

  const stepIndex = ONBOARDING_STEPS.indexOf(currentStep);
  const totalSteps = ONBOARDING_STEPS.length - 1; // Exclude 'completed'
  const progressPercent = Math.round((stepIndex / totalSteps) * 100);
  const stepsRemaining = totalSteps - stepIndex;

  return (
    <div className="min-h-screen bg-cacao-dark py-12">
      <div className="mx-auto max-w-2xl px-4">
        {/* Enhanced Progress Header */}
        <div className="mb-8">
          {/* Progress bar with percentage */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white">
                Step {stepIndex + 1} of {totalSteps}
              </span>
              <span className="text-sm text-cacao-gold font-semibold">
                {progressPercent}% complete
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-cacao-dark-tertiary overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-cacao-gold to-amber-500 transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-cacao-muted">
              {stepsRemaining === 1 ? 'Almost done! Just one more step.' :
               stepsRemaining > 0 ? `${stepsRemaining} steps remaining • About ${stepsRemaining} minute${stepsRemaining > 1 ? 's' : ''} left` :
               'All set!'}
            </p>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-between">
            {ONBOARDING_STEPS.slice(0, -1).map((step, i) => (
              <div key={step} className="flex items-center">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-all duration-300 ${
                    i < stepIndex
                      ? 'bg-cacao-gold text-cacao-dark scale-100'
                      : i === stepIndex
                      ? 'border-2 border-cacao-gold text-cacao-gold scale-110 shadow-lg shadow-cacao-gold/30'
                      : 'border-2 border-cacao-dark-tertiary text-cacao-text-tertiary scale-90'
                  }`}
                >
                  {i < stepIndex ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                {i < ONBOARDING_STEPS.length - 2 && (
                  <div
                    className={`mx-1 h-0.5 flex-1 min-w-[20px] transition-all duration-300 ${
                      i < stepIndex ? 'bg-cacao-gold' : 'bg-cacao-dark-tertiary'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6 page-enter">
          <div className="flex items-center gap-3 mb-1">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-cacao-gold/20 text-cacao-gold text-sm font-bold">
              {stepIndex + 1}
            </span>
            <h1 className="font-heading text-xl font-semibold text-white">{STEP_LABELS[currentStep]}</h1>
          </div>

          <div className="mt-5 space-y-4">
            {currentStep === 'account' && (
              <p className="text-sm text-cacao-muted">
                Welcome to Traction Bee. Let us set up your account.
              </p>
            )}

            {currentStep === 'founder_profile' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">Full name</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => updateField('full_name', e.target.value)}
                    className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">LinkedIn URL</label>
                  <input
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => updateField('linkedin_url', e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                  />
                </div>
              </>
            )}

            {currentStep === 'company_profile' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">Company name</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => updateField('company_name', e.target.value)}
                    className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">Industry</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {INDUSTRIES.map((ind) => (
                      <button
                        key={ind}
                        type="button"
                        onClick={() => toggleArrayField('company_industry', ind)}
                        className={`rounded-full px-3 py-1 text-xs ${
                          formData.company_industry.includes(ind)
                            ? 'bg-cacao-gold text-cacao-dark'
                            : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold'
                        } transition-colors`}
                      >
                        {ind}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {currentStep === 'icp_definition' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">Vertical niche (your specialty)</label>
                  <p className="mt-1 text-xs text-cacao-text-tertiary">Select the primary market you serve</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {VERTICAL_NICHES.map((niche) => (
                      <button
                        key={niche}
                        type="button"
                        onClick={() => updateField('vertical_niche', niche)}
                        className={`rounded-full px-3 py-1 text-xs ${
                          formData.vertical_niche === niche
                            ? 'bg-cacao-gold text-cacao-dark'
                            : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold'
                        } transition-colors`}
                      >
                        {niche}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">Target industries</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {INDUSTRIES.map((ind) => (
                      <button
                        key={ind}
                        type="button"
                        onClick={() => toggleArrayField('target_industries', ind)}
                        className={`rounded-full px-3 py-1 text-xs ${
                          formData.target_industries.includes(ind)
                            ? 'bg-cacao-gold text-cacao-dark'
                            : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold'
                        } transition-colors`}
                      >
                        {ind}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">Company sizes</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {COMPANY_SIZES.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleArrayField('target_company_sizes', size)}
                        className={`rounded-full px-3 py-1 text-xs ${
                          formData.target_company_sizes.includes(size)
                            ? 'bg-cacao-gold text-cacao-dark'
                            : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold'
                        } transition-colors`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">Target roles</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {TARGET_ROLES.map((role) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => toggleArrayField('target_roles', role)}
                        className={`rounded-full px-3 py-1 text-xs ${
                          formData.target_roles.includes(role)
                            ? 'bg-cacao-gold text-cacao-dark'
                            : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold'
                        } transition-colors`}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {currentStep === 'outreach_preferences' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">Tone of voice</label>
                  <div className="mt-2 flex gap-2">
                    {TONES_OF_VOICE.map((tone) => (
                      <button
                        key={tone.value}
                        type="button"
                        onClick={() => updateField('tone_of_voice', tone.value as 'formal' | 'casual' | 'friendly')}
                        className={`rounded-md px-3 py-2 text-xs ${
                          formData.tone_of_voice === tone.value
                            ? 'bg-cacao-gold text-cacao-dark'
                            : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold'
                        } transition-colors`}
                      >
                        {tone.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">Email signature</label>
                  <textarea
                    value={formData.email_signature}
                    onChange={(e) => updateField('email_signature', e.target.value)}
                    rows={3}
                    className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-6">
            <button
              onClick={handleNext}
              disabled={loading}
              className="w-full rounded-md bg-cacao-gold px-3 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : currentStep === 'outreach_preferences' ? 'Complete setup' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
